import React, { FunctionComponent, useEffect, useState } from 'react';
import { Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { connect, ConnectedProps } from 'react-redux';
import Apis from '../apis';
import { HeaderDisplayKeysWithType, InlineEditableTable } from '../components';
import { PHISight, SimpleStudent } from '../model';
import { ErrorDispatches } from '../redux/Dispatches';
import { Nullable } from '../types';
import { ClassSem, WrapClassBaseLayout } from '../layouts';
import { ApplicationState } from '../redux/States';
import {
  denormalizeStudentSight,
  normalizeStudentSight,
  SightTableHeader,
  ClassSightCalculators,
} from './TableHeaders/SightHeader';
import apis from '../apis';

type EyeSightPageData = SimpleStudent & Nullable<PHISight>;

const columns = [
  'isDilated',
  'sight0R',
  'sight0L',
  'sightR',
  'sightL',
  'examination',
  'examinationState'
];
const modalDisplayHeader: HeaderDisplayKeysWithType<EyeSightPageData>[] = [
  { property: 'seat', display: '座號' },
  { property: 'name', display: '學生' },
  ...(SightTableHeader as HeaderDisplayKeysWithType<EyeSightPageData>[]).filter(
    (h) => columns.some((col) => col === h.property)
  ),
];
const mapState = (state: ApplicationState, ownProps: ClassSem) => ({
  ...ownProps,
});
const mapDispatch = { ...ErrorDispatches };
const connector = connect(mapState, mapDispatch);

type Props = ConnectedProps<typeof connector>;

const classPHISightPage: FunctionComponent<Props> = ({
  classId,
  year,
  sem,
  catchErrorForModal,
}) => {
  const [students, setStudent] = useState([] as EyeSightPageData[]);
  const [toggle, setToggle] = useState(false);

  useEffect(() => {
    // TODO: fetch sight manage map from server
    // updateSightManagerMap({});
    // setSightManageMap();
  }, []);

  useEffect(() => {
    refreshStudents();
  }, [classId, year, sem, toggle]);

  function refreshStudents() {
    if (classId && year && sem) {
      Apis.getClassStudentSight(classId, year, sem)
        .then((ss) =>
          setStudent(
            ss.map(({ sight, ...s }) => ({
              ...s,
              ...sight,
            }))
          )
        )
        .catch(catchErrorForModal);
    } else {
      setStudent([]);
    }
  }

  function onDelete(sight: EyeSightPageData) {
    toast
      .promise(apis.deleteStudentSight(sight.pid, year, sem), {
        pending: '視力資料刪除中......',
        success: '視力資料刪除成功！',
        error: '視力資料刪除失敗！請查看錯誤資訊。',
      })
      .then(() => setToggle(!toggle))
      .catch(catchErrorForModal);
  }

  // function onUpdateStudentSight(sight: PHISight, passNext: boolean) {
  //   if (editingStudent && editingStudent.pid) {
  //     // const manageIds = normalizeManageId(manageIdStr);
  //     // sight.manageID = manageIds;
  //     if(sight.manageID?.length) {
  //       const id = sight.manageID[0];
  //       sight.manage = manageIDMap[id];
  //     }
  //     toast
  //       .promise(Apis.InsertStudentSight(editingStudent?.pid, sight), {
  //         pending: '視力資料上傳中......',
  //         success: '視力資料上傳成功！',
  //         error: '視力資料上傳失敗！請查看錯誤資訊。',
  //       })
  //       .then(() => {
  //         if (passNext) {
  //           const currentIdx = students.findIndex(
  //             (s) => s.pid === editingStudent?.pid
  //           );
  //           const nextStudent = students[currentIdx + 1];
  //           if (nextStudent) {
  //             setEditingStudent({ ...nextStudent, passNext: false });
  //             setManageIdStr(nextStudent.manageID?.join(',') || '');
  //           }
  //         } else {
  //           onHide();
  //         }
  //       })
  //       .catch(catchErrorForModal);
  //   }
  function onUpdateStudentSight(pid: string, sight: PHISight) {
    toast.dismiss();
    toast
      .promise(Apis.InsertStudentSight(pid, sight), {
        pending: '視力資料上傳中......',
        success: '視力資料上傳成功！',
        error: '視力資料上傳失敗！請查看錯誤資訊。',
      })
      .then(() => {
        setToggle(!toggle);
      })
      .catch(catchErrorForModal);
  }

  return (
    <React.Fragment>
      <Row>
        <div
          className="mt-3 p-3"
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            fontSize: '15px',
            lineHeight: '1.6',
            width: '100%',
            marginBottom: '10px',
          }}
        >
          <div>
            <strong>視力輸入說明：</strong>
          </div>

          <div>
            1️⃣ 視力請直接輸入整數，例如 0.8 請輸入 8；視力值＜0.1 請輸入 -1。
          </div>

          <div>
            2️⃣
            「散瞳治療」是指點長效型散瞳眼藥治療；「散瞳」是指檢驗屈光度數前的短效散瞳。
          </div>

          <div>
            3️⃣ 裸眼視力無法測量代號：-8 戴隱形眼鏡；-7 角膜塑型；-6 雷射治療；-5
            全盲
          </div>

          <div>4️⃣ 無法表達或在家自學僅輸入裸視右眼為 -9。</div>

          <div>
            5️⃣ 「定期檢查」日期格式：1999/01/01，或點兩下顯示萬年曆選單。
          </div>

          <div>
            6️⃣ 如「近視右（左）」或「遠視右（左）」有小數點，
            請將小數點四捨五入至個位數後以整數輸入。
          </div>
        </div>
      </Row>
      <Row>
        <div className="mt-3 mb-1 d-flex">
          <div
            style={{
              backgroundColor: '#cacaff',
              width: '25px',
              height: '25px',
            }}
          ></div>
          此顏色代表初檢輸入欄位
        </div>
      </Row>
      <Row>
        <InlineEditableTable
          fixCol={2}
          decorators={ClassSightCalculators}
          headers={modalDisplayHeader}
          values={students}
          autoNext={true}
          onValueUpdate={(value) => {
            const normalizedSight = normalizeStudentSight(value);

            const isUnexamined =
              Number(normalizedSight.sight0R) === -9;

            if (isUnexamined && !value.examination) {
              toast.error('請選擇未受檢原因');
              return;
            }

            onUpdateStudentSight(value.pid, {
              ...value,
              ...normalizedSight,
              id: 0,
              sem,
              year,
              yearClassId: 0,
              examination: isUnexamined
                ? value.examination
                : null,
              examinationState: isUnexamined
                ? value.examinationState
                : null,
            });
          }}
          onValueDelete={onDelete}
          onRowRender={(v) => ({ deleteable: true, editable: true })}
          beforeEdit={(v) => ({ ...v, ...denormalizeStudentSight(v) })}
        />
      </Row>
    </React.Fragment>
  );
};

export const ClassPHISimpleSightPage = connector(
  WrapClassBaseLayout(classPHISightPage)
);
